import { GET_COMPANY } from "@apiUtils";
import type { IPagedResponse } from "@atlas/sdk";
import { mutationFetch } from "@atlas/sdk";
import type { IAddUserRequest, IUpdateUserRequest, IUserInfo } from "@models";
import type {
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const STANDARD_USERS_KEY = "standardUsersKey";

export interface ICompanyUsersRequest {
  companyUuid: string;
  size?: number;
  page?: number;
  emailSearch?: string;
}

export const getCompanyUsersKey = (
  request: ICompanyUsersRequest,
  basePath?: string,
) => [STANDARD_USERS_KEY, request, basePath] as const;

export const useCompanyUsers = (
  request: ICompanyUsersRequest,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      IPagedResponse<IUserInfo>,
      AxiosError,
      IPagedResponse<IUserInfo>,
      ReturnType<typeof getCompanyUsersKey>
    >
  >,
) => {
  const { companyUuid } = request;
  return useQuery({
    queryKey: getCompanyUsersKey(request, basePath),
    queryFn: async () => {
      const path = `${GET_COMPANY}/${companyUuid}/users`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      const data = await mutationFetch<IPagedResponse<IUserInfo>>({
        url,
        method: "GET",
        params: request,
      });
      return data;
    },
    enabled: !!companyUuid,
    ...options,
  });
};

export const useCompanyUsersInfinite = (
  request: ICompanyUsersRequest,
  basePath?: string,
  options?: Partial<
    UseInfiniteQueryOptions<
      IPagedResponse<IUserInfo>,
      AxiosError,
      IPagedResponse<IUserInfo>,
      ReturnType<typeof getCompanyUsersKey>,
      number
    >
  >,
) => {
  const { companyUuid, page = 0 } = request;
  return useInfiniteQuery({
    queryKey: getCompanyUsersKey(request, basePath),
    queryFn: async ({ pageParam }) => {
      const path = `${GET_COMPANY}/${companyUuid}/users`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      return mutationFetch<IPagedResponse<IUserInfo>>({
        url,
        method: "GET",
        params: {
          ...request,
          page: pageParam,
        },
      });
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.number + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    getPreviousPageParam: (firstPage) =>
      firstPage.number > 0 ? firstPage.number - 1 : undefined,
    enabled: !!companyUuid,
    ...options,
  });
};

export const addUser = async (
  companyUuid: string,
  request: IAddUserRequest,
  basePath?: string,
): Promise<IUserInfo> => {
  const path = `${GET_COMPANY}/${companyUuid}/users`;
  const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
  return mutationFetch<IUserInfo>({
    url,
    method: "POST",
    body: request,
  });
};

export const useAddUser = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseMutationOptions<IUserInfo, AxiosError, IAddUserRequest, unknown>
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: IAddUserRequest) =>
      addUser(companyUuid ?? "", request, basePath),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};

export const updateUser = async (
  companyUuid: string,
  userId: string,
  request: IUpdateUserRequest,
  basePath?: string,
): Promise<IUserInfo> => {
  const path = `${GET_COMPANY}/${companyUuid}/user/${userId}`;
  const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
  return mutationFetch<IUserInfo>({
    url,
    method: "PATCH",
    body: request,
  });
};

export const useUpdateUser = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseMutationOptions<
      IUserInfo,
      AxiosError,
      { userId: string; request: IUpdateUserRequest },
      unknown
    >
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      request,
    }: {
      userId: string;
      request: IUpdateUserRequest;
    }) => updateUser(companyUuid ?? "", userId, request, basePath),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};

export const updateUserStatus = async (
  companyUuid: string,
  userId: string,
  isActive: boolean,
  basePath?: string,
): Promise<IUserInfo> => {
  const path = `${GET_COMPANY}/${companyUuid}/user/${userId}`;
  const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
  return mutationFetch<IUserInfo>({
    url,
    method: "PATCH",
    body: { isActive },
  });
};

export const useUpdateUserStatus = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseMutationOptions<
      IUserInfo,
      AxiosError,
      { userId: string; isActive: boolean },
      unknown
    >
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      updateUserStatus(companyUuid ?? "", userId, isActive, basePath),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};
